import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowLeft, Calendar, Clock } from "lucide-react";
import Footer from "@/components/Footer";
import { useBlogPosts } from "@/hooks/useBlogPosts";
import { calculateReadTime, formatReadTime } from "@/lib/readTimeCalculator";
import { Helmet } from "react-helmet-async";
import { generatePageTitle, generateCanonicalUrl } from "@/lib/seoConfig";
import { Breadcrumbs } from "@/components/Breadcrumbs";

const Blog = () => {
  const { data: blogPosts, isLoading } = useBlogPosts("published");

  return (
    <>
      <Helmet>
        <title>{generatePageTitle("Blog")}</title>
        <meta name="description" content="Expert insights on SMS marketing, customer engagement, and business automation from the iQ Texting team. Learn how to convert more website visitors into customers." />
        <link rel="canonical" href={generateCanonicalUrl("/blog")} />
        <meta property="og:title" content={generatePageTitle("Blog")} />
        <meta property="og:description" content="Expert insights on SMS marketing, customer engagement, and business automation." />
        <meta property="og:url" content={generateCanonicalUrl("/blog")} />
        <meta name="twitter:title" content={generatePageTitle("Blog")} />
        <meta name="twitter:description" content="Expert insights on SMS marketing, customer engagement, and business automation." />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-6">
          <Button variant="ghost" asChild className="mb-4">
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>
          <Breadcrumbs items={[{ name: "Blog", url: "/blog" }]} />
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent mt-4">
            iQ Texting Blog
          </h1>
          <p className="text-muted-foreground mt-2">
            Insights, tips, and updates from the world of SMS communication
          </p>
        </div>
      </header>

      {/* Blog Content */}
      <main className="container mx-auto px-4 py-12">
        {isLoading ? (
          <p className="text-center">Loading posts...</p>
        ) : blogPosts && blogPosts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogPosts.map((post) => {
              const author = post.profiles;
              const authorName = author?.full_name || "iQ Team";
              const authorInitials = authorName
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 2);
              const readTime = calculateReadTime(post.content);

              return (
                <Card key={post.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-xl">{post.title}</CardTitle>
                    <CardDescription>{post.excerpt}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={author?.avatar_url || ""} alt={authorName} />
                          <AvatarFallback className="text-xs">{authorInitials}</AvatarFallback>
                        </Avatar>
                        {author?.author_slug ? (
                          <Link 
                            to={`/blog/author/${author.author_slug}`}
                            className="hover:text-primary transition-colors"
                          >
                            {authorName}
                          </Link>
                        ) : (
                          <span>{authorName}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(post.published_at || post.created_at).toLocaleDateString()}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatReadTime(readTime)}
                      </div>
                    </div>
                    <Button variant="link" className="px-0" asChild>
                      <Link to={`/blog/${post.slug}`}>
                        Read More →
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center mt-12 p-8 bg-muted/50 rounded-lg">
            <h2 className="text-2xl font-semibold mb-2">No Posts Yet</h2>
            <p className="text-muted-foreground">
              Check back soon for insights and updates from our team.
            </p>
          </div>
        )}
      </main>

      <Footer />
    </div>
    </>
  );
};

export default Blog;
